import { action, makeObservable, observable, override, runInAction, toJS } from "mobx";
import {
  ApiClient,
  CollectionName,
  Genome,
  GenomeInList,
  GenomesBulkDownloadPayload,
  GenomesBulkDownloadQueryResult,
  GenomesOrderBy,
  PaginatedGenomesQuery,
  RestCallResult,
} from "../api";
import { GenomesPageData } from "../utils/genomes";
import UserStore from "./user-store";
import {
  CELL_BIO_SEARCH_SECTIONS,
  SearchFilterKeys,
  SearchFilterProps,
  SearchFilters,
  SearchFilterValue,
} from "../utils/search";
import BaseGenomeStore, { observedGenome } from "./base-genome-store";
import debounce from "lodash.debounce";

type GenomeDetailsData = {
  genome: Genome | null;
  loading: boolean;
  error: boolean;
};

function getDefaultListData(): GenomesPageData {
  return {
    error: false,
    genomes: observable.array([], { deep: false }),
    loading: false,
    pagination: { page: 1 },
  };
}

function getDefaultDetailsData(): GenomeDetailsData {
  return {
    genome: null,
    loading: false,
    error: false,
  };
}

export default class CellBioStore extends BaseGenomeStore {
  @observable public listData: GenomesPageData = getDefaultListData();
  @observable public detailsData: GenomeDetailsData = getDefaultDetailsData();

  constructor(api: ApiClient, userStore: UserStore, ssrData?: CellBioStore) {
    super(api, userStore, ssrData);
    makeObservable(this);

    if (ssrData) {
      this.listData.genomes.replace(ssrData.listData.genomes.map((g) => observedGenome(g)));
      this.listData.loading = false;
      this.listData.error = ssrData.listData.error;
      this.listData.pagination = { ...ssrData.listData.pagination };

      this.detailsData = ssrData.detailsData;
    }
  }

  get searchSections(): SearchFilterProps[] {
    return CELL_BIO_SEARCH_SECTIONS;
  }

  get pageNum(): number | undefined {
    return this.listData.pagination.page;
  }

  get defaultCollectionNames(): CollectionName[] {
    return ["human_cells", "animal_cells", "organoids"];
  }

  protected setGenomeStarring(uuid: string, starred: boolean) {
    for (const genome of this.listData.genomes) {
      if (genome.uuid === uuid) {
        genome.starred = starred;
        break;
      }
    }
  }

  @action
  serverSideInitialFetch = async (
    collectionName: CollectionName | null,
    query: { [key: string]: string[] | string | undefined }
  ) => {
    const searchParams = new URLSearchParams();
    for (const key of Object.keys(query)) {
      const val = query[key];
      if (!val) {
        continue;
      }
      if (typeof val === "string") {
        searchParams.set(key, val);
      } else {
        val.forEach((x) => searchParams.set(key, x));
      }
    }
    this.setVariablesFromQuery(searchParams);
    this.setCollectionName(collectionName);
    await this.fetchList();
    runInAction(() => {
      this.hydrated = true;
    });
  };

  @action
  setCollectionName = (newName: CollectionName | null) => {
    this.collectionName = newName;
  };

  @action
  setOrderBy = (orderBy: GenomesOrderBy) => {
    this.baseSetOrderBy(orderBy);
    this.fetchList();
  };

  @action
  updateIfNotHydrated = (collectionName: CollectionName | null, searchParams: URLSearchParams) => {
    if (this.hydrated) {
      // Data already updated via SSR. But next time we will need to fetch it.
      // So marking itself as non-hydrated.
      this.hydrated = false;
    } else {
      this.setVariablesFromQuery(searchParams);
      this.collectionName = collectionName;
      this.fetchList();
      this.selectAllActivated = false;
      this.selectionUuids.clear();
    }
  };

  // Only set in the all page
  @action
  setVariablesFromQuery = (params: URLSearchParams) => {
    this.baseSetVariablesFromQuery(Object.fromEntries(params));
    const page = params.get("page") as string;
    this.listData.pagination.page = page ? parseInt(page, 10) : 1;
  };

  @action
  setPage = (newPage: number) => {
    this.listData.pagination.page = newPage;
    this.fetchList();
  };

  @action
  fetchList = async () => {
    const collection_name: CollectionName[] = this.collectionName
      ? [this.collectionName]
      : this.defaultCollectionNames;
    const payload: Omit<PaginatedGenomesQuery, "search_object"> & { search_object: SearchFilters } =
      {
        order_by: this.orderBy,
        ascending: this.ascending,
        collection_name,
        mine_only: this.mineOnly,
        search_object: toJS(this.searchObject),
      };
    const page = this.listData.pagination.page || 1;

    this.listData.loading = true;
    const result = await this.api.post<GenomeInList[]>(
      "/genome-portal-api/genomes",
      payload,
      {},
      { page: page }
    );

    runInAction(() => {
      if (result.error !== undefined) {
        this.listData.error = true;
        this.listData.genomes.clear();
      } else {
        this.listData.error = false;
        this.listData.genomes.replace(result.data.map((g) => observedGenome(g)));
        this.listData.pagination = JSON.parse(result.headers.get("X-Pagination")!);
      }
      this.listData.loading = false;
    });
  };

  private debouncedFetchList = debounce(this.fetchList, 250);

  @action
  fetchDetails = async (uuid: string) => {
    runInAction(() => {
      this.detailsData.loading = true;
    });
    const result = await this.api.get<Genome>("/genome-portal-api/genomes/{uuid}", {
      uuid,
    });
    runInAction(() => {
      if (result.error === undefined) {
        this.detailsData.error = false;
        this.detailsData.genome = observable(result.data);
      } else {
        this.detailsData.error = true;
        this.detailsData.genome = null;
      }
      this.detailsData.loading = false;
    });
  };

  @action
  fetchDetailsIfNeeded = async (uuid: string) => {
    if (
      !this.detailsData.loading &&
      !this.detailsData.error &&
      this.detailsData.genome?.uuid !== uuid
    ) {
      await this.fetchDetails(uuid);
    }
  };

  @action
  resetDetailsData = () => {
    this.detailsData.error = false;
    this.detailsData.loading = false;
    this.detailsData.genome = null;
  };

  @override
  addSearchFilter<K extends keyof SearchFilters>(key: K, val: string): void {
    super.addSearchFilter(key, val);

    this.listData.pagination.page = 1;
    this.listData.genomes.clear();
    this.listData.loading = true;
    this.debouncedFetchList();
  }

  @override
  removeSearchFilter(key: SearchFilterKeys, val: SearchFilterValue): void {
    super.removeSearchFilter(key, val);

    this.listData.pagination.page = 1;
    this.listData.genomes.clear();
    this.fetchList();
  }

  getNumSelected = (): { numSelected: number; numTotal: number } => {
    const numTotal = this.listData.pagination.total ?? this.listData.genomes.length;
    const selectedSize = this.selectionUuids.size;
    if (this.selectAllActivated) {
      return { numSelected: numTotal - selectedSize, numTotal };
    }
    return { numSelected: selectedSize, numTotal };
  };

  initBulkSearch = async (
    downloadTypes: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<GenomesBulkDownloadQueryResult>> => {
    return await this.baseSearchBulkDownload(downloadTypes, {});
  };

  initBulkDownload = async (
    downloadTypes: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<unknown>> => {
    return await this.baseInitBulkDownload(downloadTypes, {});
  };
}
